/**
 * Zod schemas for flashcard import and validation
 */

import { z } from 'zod'

/**
 * Schema for a single flashcard import
 * Supports single words, phrases, and lyrics (1-2 lines per side)
 */
export const flashcardImportSchema = z.object({
  title: z.string().optional(), // Optional title for reference
  // Support both single string and array formats
  frontSpanish: z.union([
    z.string(), // Single line or word
    z.array(z.string()).min(1).max(2), // 1-2 lines
  ]).transform((val) => Array.isArray(val) ? val : [val]),
  backEnglish: z.union([
    z.string(), // Single line or word
    z.array(z.string()).min(1).max(2), // 1-2 lines
  ]).transform((val) => Array.isArray(val) ? val : [val]),
  // Card type detection
  cardType: z.enum(['word', 'phrase', 'lyrics']).optional(),
  // Enhanced fields
  targetVerb: z.string().optional(), // Spanish verb infinitive
  sourceVerb: z.string().optional(), // English meaning of the verb
  difficulty: z.enum(['easy', 'medium', 'hard']).optional(),
  tags: z.array(z.string()).optional(),
  notes: z.string().optional(),
  priority: z.number().min(0).max(10).optional().default(5),
  // Support legacy song structure
  sections: z.array(z.object({
    type: z.string(),
    spanish: z.string(),
    english: z.string(),
  })).optional(),
})

/**
 * Schema for deck import with multiple flashcards
 */
export const deckImportSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  flashcards: z.array(flashcardImportSchema),
})

/**
 * Type exports
 */
export type FlashcardImport = z.infer<typeof flashcardImportSchema>
export type DeckImport = z.infer<typeof deckImportSchema>

/**
 * Helper function to convert song structure to flashcard structure
 */
export function convertSongToFlashcards(song: {
  title: string
  sections: Array<{ type: string; spanish: string; english: string }>
  targetVerb?: string
  sourceVerb?: string
}): FlashcardImport[] {
  const flashcards: FlashcardImport[] = []

  // Create flashcards from each section (or combine sections)
  for (let i = 0; i < song.sections.length; i += 2) {
    const section1 = song.sections[i]
    const section2 = song.sections[i + 1]

    const frontSpanish: string[] = [section1.spanish]
    const backEnglish: string[] = [section1.english]

    if (section2) {
      frontSpanish.push(section2.spanish)
      backEnglish.push(section2.english)
    }

    const isSingleWord = frontSpanish.length === 1 && frontSpanish[0].split(' ').length === 1

    flashcards.push({
      title: `${song.title} - ${section1.type}`,
      frontSpanish,
      backEnglish,
      cardType: isSingleWord ? ('word' as const) : ('lyrics' as const),
      targetVerb: song.targetVerb,
      sourceVerb: song.sourceVerb,
      priority: 5,
    })
  }

  return flashcards
}

